import { TAddQtyTableData } from '@monorepo/types';
import { TTableCellType, TTableInfo, TTableTreeModel, TTextCell } from '../../../table.type';
import { ModifiersZTableModel } from '../../../table.modifier';
import { ExternalInputPadUtil } from '../../utils/external-input-pad-util';
import { TRowActionEvent } from '../../..';

const createHeaderRow = (
  isMultiSizeSupported: boolean,
  qtyTotal: number,
  sampleQtyTotal: number,
): TTableInfo['header']['cells'] => {
  return [
    {
      type: 'TEXT_CELL',
      text: '',
      widthInPx: 4,
    },
    {
      type: 'TEXT_CELL',
      text: 'SIZE',
      widthInPx: 10,
    },
    {
      type: 'TEXT_CELL',
      text: 'QTY',
      widthInPx: 10,
      ...(isMultiSizeSupported && {
        subtitle: {
          text: `Total: ${qtyTotal}`,
          align: 'BLOCK',
        },
      }),
    },
    {
      type: 'TEXT_CELL',
      text: 'SAMPLE(Auto-populated)',
      widthInPx: 10,
      ...(isMultiSizeSupported && {
        subtitle: {
          text: `Total: ${sampleQtyTotal}`,
        },
      }),
    },
    {
      type: 'ACTION_CELL',
      actionType: 'DELETE',
      stickyType: 'RIGHT',
      widthInPx: 10,
    },
  ];
};

const getRows = (rowItem: TAddQtyTableData, isMultiSizeSupported: boolean) => {
  const rows = [
    {
      type: 'TEXT_CELL',
      text: rowItem.index,
    },
    {
      type: 'TEXT_CELL',
      text: rowItem.size,
    },
    {
      type: 'TEXT_CELL',
      text: rowItem.qty,
      isExternalInput: {
        editStatus: rowItem.index == 1 ? true : false,
      },
    },
    {
      type: 'TEXT_CELL',
      ...(isMultiSizeSupported && {
        isExternalInput: {
          editStatus: false,
        },
      }),
      text: rowItem.sample_qty,
    },
    {
      type: 'ACTION_CELL',
      actionType: 'DELETE',
      asIcon: true,
      stickyType: 'RIGHT',
    },
  ] as TTableCellType[];

  return rows;
};

export const SELECT_QTY_LISTING_TABLE_MODEL = {
  createModel: (
    rowInfo: TAddQtyTableData[],
    isMultiSizeSupported: boolean,
    totalPreSelectedQty: number,
    totalPreSelectedTotalSample: number,
  ) => {
    const tableModel = ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        headerConfig: createHeaderRow(isMultiSizeSupported, totalPreSelectedQty, totalPreSelectedTotalSample),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRows(item, isMultiSizeSupported),
          rowSelectionInfo: {
            isSelected: item.index == 1 ? true : false,
            isDisabled: false,
          },
        };
      },
    );
    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(tableModel);

    const initialSelectedExternalInput = tableModel.tableInfo.rows[0]?.cells[2] as TTextCell;
    const numberPadValue = parseInt(initialSelectedExternalInput?.text as string);

    return {
      model: tableModel,
      currentActiveRowCellIndex: currentActiveRowCellIndex,
      numberPadValue: numberPadValue,
    };
  },
  updateHeaderQtyAndSampleQtyTotal<TAddQtyTableData>(
    tableModel: TTableTreeModel<TAddQtyTableData>,
    qtyTotal: number,
    sampleQtyTotal: number,
  ) {
    const updateModel = { ...tableModel };

    const updatedHeaderData = createHeaderRow(true, qtyTotal, sampleQtyTotal);

    updateModel.tableInfo.header.cells = [...updatedHeaderData];
    updateModel.options!.headerConfig = updatedHeaderData;

    return updateModel;
  },
  updateSampleQtyForSingleSize<TAddQtyTableData>(
    tableModel: TTableTreeModel<TAddQtyTableData>,
    sampleQtyTotal: number,
  ) {
    const updateModel = { ...tableModel };
    const updatedHeaderData = createHeaderRow(false, 0, 0);

    updateModel.tableInfo.header.cells = [...updatedHeaderData];
    updateModel.options!.headerConfig = updatedHeaderData;

    (updateModel.tableInfo.rows[0].cells[3] as TTextCell).text = sampleQtyTotal;
    return updateModel;
  },
  deleteRowAndUpdateTable<TAddQtyTableData>(tableModel: TTableTreeModel<TAddQtyTableData>, size: string) {
    const updateModel = { ...tableModel };
    updateModel.tableInfo.rows = updateModel.tableInfo.rows.filter((row) => (row.cells[1] as TTextCell).text !== size);
    const selectedRowCellIndexAfterRowDelete = { rowIndex: 0, cellIndex: 2 };
    const modelWithNumberPadData = ExternalInputPadUtil.selectExtEditableInputBasedOnRowCellIndex(
      updateModel,
      selectedRowCellIndexAfterRowDelete,
    );

    return {
      model: modelWithNumberPadData.model,
      currentActiveRowCellIndex: selectedRowCellIndexAfterRowDelete,
      numberPadValue: modelWithNumberPadData.numberPadValue,
    };
  },
};
